import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  OpportuniteHistoriqueRow,
  OpportuniteRow,
  OpportuniteStatut,
  Json,
} from '@/lib/database.types';

export interface OpportunitesFilter {
  entrepriseId?: string | null;
  pipelineId?: string | null;
  etapeId?: string | null;
  statut?: OpportuniteStatut | null;
  commercialId?: string | null;
  activiteId?: string | null;
  tierId?: string | null;
  search?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  amountMin?: number | null;
  amountMax?: number | null;
  page?: number;
  pageSize?: number;
}

export function useOpportunites(filter: OpportunitesFilter) {
  const pageSize = filter.pageSize ?? 50;
  const page = filter.page ?? 0;
  return useQuery({
    queryKey: ['crm_opportunites', filter],
    queryFn: async (): Promise<{ rows: OpportuniteRow[]; count: number }> => {
      let q = supabase
        .from('opportunites')
        .select('*', { count: 'exact' })
        .order('date_creation', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);
      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.pipelineId) q = q.eq('pipeline_id', filter.pipelineId);
      if (filter.etapeId) q = q.eq('etape_id', filter.etapeId);
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.commercialId) q = q.eq('commercial_id', filter.commercialId);
      if (filter.activiteId) q = q.eq('activite_id', filter.activiteId);
      if (filter.tierId) q = q.eq('tier_id', filter.tierId);
      if (filter.dateFrom) q = q.gte('date_creation', filter.dateFrom);
      if (filter.dateTo) q = q.lte('date_creation', filter.dateTo);
      if (filter.amountMin != null) q = q.gte('montant_estime_dzd', filter.amountMin);
      if (filter.amountMax != null) q = q.lte('montant_estime_dzd', filter.amountMax);
      if (filter.search && filter.search.length >= 2) {
        const s = filter.search.replace(/%/g, '');
        q = q.or(`reference.ilike.%${s}%,titre.ilike.%${s}%`);
      }
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: data as unknown as OpportuniteRow[], count: count ?? 0 };
    },
    placeholderData: (prev) => prev,
  });
}

export function useOpportunite(id: string | null) {
  return useQuery({
    queryKey: ['crm_opportunite', id],
    enabled: !!id,
    queryFn: async (): Promise<OpportuniteRow | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('opportunites')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as OpportuniteRow | null) ?? null;
    },
  });
}

export interface UpsertOpportuniteInput {
  id?: string;
  pipeline_id: string;
  etape_id: string;
  entreprise_id: string;
  activite_id?: string | null;
  tier_id?: string | null;
  contact_principal_id?: string | null;
  titre: string;
  description?: string | null;
  montant_estime?: number;
  devise?: string;
  taux_change?: number;
  probabilite?: number;
  date_cloture_prevue?: string | null;
  statut?: OpportuniteStatut;
  motif_perte_id?: string | null;
  commercial_id?: string | null;
  source?: string | null;
}

export function useUpsertOpportunite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertOpportuniteInput): Promise<OpportuniteRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('opportunites')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as OpportuniteRow;
      }
      const { data, error } = await supabase.from('opportunites').insert(rest).select('*').single();
      if (error) throw error;
      return data as unknown as OpportuniteRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crm_opportunites'] });
      void qc.invalidateQueries({ queryKey: ['crm_opportunite'] });
      void qc.invalidateQueries({ queryKey: ['crm_dashboard'] });
      void qc.invalidateQueries({ queryKey: ['crm_forecasting'] });
    },
  });
}

export function useChangeEtape() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; etape_id: string }): Promise<void> => {
      const { error } = await supabase
        .from('opportunites')
        .update({ etape_id: input.etape_id })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crm_opportunites'] });
      void qc.invalidateQueries({ queryKey: ['crm_opportunite'] });
      void qc.invalidateQueries({ queryKey: ['crm_historique'] });
    },
  });
}

export function useMarquerPerdue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      motif_perte_id: string;
      etape_lost_id: string;
    }): Promise<void> => {
      const { error } = await supabase
        .from('opportunites')
        .update({
          etape_id: input.etape_lost_id,
          statut: 'lost',
          motif_perte_id: input.motif_perte_id,
        })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crm_opportunites'] });
      void qc.invalidateQueries({ queryKey: ['crm_opportunite'] });
    },
  });
}

export function useDeleteOpportunite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('opportunites').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm_opportunites'] }),
  });
}

// Champs custom : valeurs
export function useChampsValues(opportuniteId: string | null) {
  return useQuery({
    queryKey: ['crm_champs_values', opportuniteId],
    enabled: !!opportuniteId,
    queryFn: async (): Promise<Record<string, Json>> => {
      if (!opportuniteId) return {};
      const { data, error } = await supabase
        .from('opportunites_champs_values')
        .select('champ_id, valeur')
        .eq('opportunite_id', opportuniteId);
      if (error) throw error;
      const m: Record<string, Json> = {};
      for (const r of data ?? [])
        m[(r as { champ_id: string }).champ_id] = (r as { valeur: Json }).valeur;
      return m;
    },
  });
}

export function useUpsertChampValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      opportunite_id: string;
      champ_id: string;
      valeur: Json;
    }): Promise<void> => {
      const { error } = await supabase.from('opportunites_champs_values').upsert(input);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['crm_champs_values', vars.opportunite_id] });
    },
  });
}

// Historique
export function useHistorique(opportuniteId: string | null) {
  return useQuery({
    queryKey: ['crm_historique', opportuniteId],
    enabled: !!opportuniteId,
    queryFn: async (): Promise<OpportuniteHistoriqueRow[]> => {
      if (!opportuniteId) return [];
      const { data, error } = await supabase
        .from('opportunites_historique')
        .select('*')
        .eq('opportunite_id', opportuniteId)
        .order('date', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data as unknown as OpportuniteHistoriqueRow[];
    },
  });
}

export interface BulkResult {
  success: string[];
  errors: { id: string; message: string }[];
}

export function useBulkUpdateCommercial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ids: string[];
      commercial_id: string | null;
    }): Promise<BulkResult> => {
      const result: BulkResult = { success: [], errors: [] };
      const { data, error } = await supabase
        .from('opportunites')
        .update({ commercial_id: input.commercial_id })
        .in('id', input.ids)
        .select('id');
      if (error) {
        for (const id of input.ids) result.errors.push({ id, message: error.message });
      } else {
        for (const r of data ?? []) result.success.push((r as { id: string }).id);
      }
      return result;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm_opportunites'] }),
  });
}
